import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  History,
  Loader2,
  Search,
  Calendar,
  User,
  Building2,
  Clock,
  FileText,
  Download,
  Filter,
  Eye,
  Phone,
  Mail,
  Car,
  Briefcase
} from "lucide-react";

export default function HistorialVisitasPage() {
  const [visitas, setVisitas] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [tiposVisita, setTiposVisita] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedVisita, setSelectedVisita] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    fechaDesde: "",
    fechaHasta: "",
    departamento_id: "",
    tipo_visita_id: ""
  });
  
  const { getAuthHeaders } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      params.append("estado", "todos");
      if (filters.fechaDesde) params.append("fecha_desde", filters.fechaDesde);
      if (filters.fechaHasta) params.append("fecha_hasta", filters.fechaHasta);
      if (filters.departamento_id) params.append("departamento_id", filters.departamento_id);
      if (filters.tipo_visita_id) params.append("tipo_visita_id", filters.tipo_visita_id);
      
      const [visitasRes, deptRes, tiposRes] = await Promise.all([
        axios.get(`${API}/visitantes?${params.toString()}`, getAuthHeaders()),
        axios.get(`${API}/departamentos`, getAuthHeaders()),
        axios.get(`${API}/visitantes/tipos`, getAuthHeaders())
      ]);
      
      setVisitas(visitasRes.data || []);
      setDepartamentos(deptRes.data || []);
      setTiposVisita(tiposRes.data || []);
    } catch (err) {
      toast.error("Error al cargar historial de visitas");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.fechaDesde) params.append("fecha_desde", filters.fechaDesde);
      if (filters.fechaHasta) params.append("fecha_hasta", filters.fechaHasta);
      if (filters.departamento_id) params.append("departamento_id", filters.departamento_id);
      if (filters.tipo_visita_id) params.append("tipo_visita_id", filters.tipo_visita_id);
      
      const response = await axios.get(`${API}/visitantes/exportar?${params.toString()}`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `historial_visitas_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Reporte exportado correctamente");
    } catch (err) {
      toast.error("Error al exportar reporte");
    }
  };

  const handleViewDetail = (visita) => {
    setSelectedVisita(visita);
    setShowDetailDialog(true);
  };

  const filteredVisitas = visitas.filter(v => {
    const search = searchTerm.toLowerCase();
    return (
      v.nombre?.toLowerCase().includes(search) ||
      v.cedula?.toLowerCase().includes(search) ||
      v.empresa?.toLowerCase().includes(search) ||
      v.motivo?.toLowerCase().includes(search)
    );
  });

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("es-DO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const calcularDuracion = (entrada, salida) => {
    if (!entrada || !salida) return "-";
    const diff = new Date(salida) - new Date(entrada);
    const horas = Math.floor(diff / (1000 * 60 * 60));
    const minutos = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${horas}h ${minutos}m`;
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="historial-visitas-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <History className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Historial de Visitas
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Consulta y reportes de visitas
              </p>
            </div>
          </div>
          <Button 
            onClick={handleExportExcel}
            className="bg-green-600 hover:bg-green-700"
            data-testid="export-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Exportar Excel
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Filter className="w-4 h-4" />
              Filtros
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="md:col-span-2 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Buscar por nombre, cédula, empresa..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Desde</Label>
                <Input
                  type="date"
                  value={filters.fechaDesde}
                  onChange={(e) => setFilters({ ...filters, fechaDesde: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Hasta</Label>
                <Input
                  type="date"
                  value={filters.fechaHasta}
                  onChange={(e) => setFilters({ ...filters, fechaHasta: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Departamento</Label>
                <Select 
                  value={filters.departamento_id} 
                  onValueChange={(v) => setFilters({ ...filters, departamento_id: v === "todos" ? "" : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    {departamentos.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results Table */}
        <Card>
          <CardHeader>
            <CardTitle>Registros de Visitas</CardTitle>
            <CardDescription>
              {filteredVisitas.length} registro(s) encontrado(s)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredVisitas.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <History className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p>No hay registros de visitas</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left p-3 font-medium text-slate-600">Visitante</th>
                      <th className="text-left p-3 font-medium text-slate-600">Empresa</th>
                      <th className="text-left p-3 font-medium text-slate-600">Departamento</th>
                      <th className="text-left p-3 font-medium text-slate-600">Tipo</th>
                      <th className="text-left p-3 font-medium text-slate-600">Entrada</th>
                      <th className="text-left p-3 font-medium text-slate-600">Salida</th>
                      <th className="text-left p-3 font-medium text-slate-600">Duración</th>
                      <th className="text-left p-3 font-medium text-slate-600">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVisitas.map((v) => (
                      <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-3">
                          <div>
                            <p className="font-medium">{v.nombre}</p>
                            <p className="text-sm text-slate-500 font-mono">{v.cedula}</p>
                          </div>
                        </td>
                        <td className="p-3">{v.empresa || "-"}</td>
                        <td className="p-3">{v.departamento_nombre || "-"}</td>
                        <td className="p-3">
                          <Badge variant="outline">{v.tipo_visita_nombre || "-"}</Badge>
                        </td>
                        <td className="p-3 text-sm">{formatDateTime(v.fecha_entrada)}</td>
                        <td className="p-3 text-sm">{formatDateTime(v.fecha_salida)}</td>
                        <td className="p-3">
                          <Badge className="bg-slate-100 text-slate-600">
                            {calcularDuracion(v.fecha_entrada, v.fecha_salida)}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleViewDetail(v)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-indigo-600" />
                Detalle de Visita
              </DialogTitle>
            </DialogHeader>
            
            {selectedVisita && (
              <div className="space-y-4">
                {/* Datos del visitante */}
                <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                  <h4 className="font-medium text-slate-900">Datos del Visitante</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-400" />
                      <span>{selectedVisita.nombre}</span>
                    </div>
                    <div className="flex items-center gap-2 font-mono">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <span>{selectedVisita.cedula}</span>
                    </div>
                    {selectedVisita.telefono && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span>{selectedVisita.telefono}</span>
                      </div>
                    )}
                    {selectedVisita.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <span>{selectedVisita.email}</span>
                      </div>
                    )}
                    {selectedVisita.empresa && (
                      <div className="flex items-center gap-2 col-span-2">
                        <Briefcase className="w-4 h-4 text-slate-400" />
                        <span>{selectedVisita.empresa}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Datos de la visita */}
                <div className="p-4 bg-indigo-50 rounded-lg space-y-2">
                  <h4 className="font-medium text-indigo-900">Datos de la Visita</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-indigo-600">Departamento:</span>
                      <p className="font-medium">{selectedVisita.departamento_nombre || "-"}</p>
                    </div>
                    <div>
                      <span className="text-indigo-600">Persona visitada:</span>
                      <p className="font-medium">{selectedVisita.persona_visita_nombre || "-"}</p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-indigo-600">Motivo:</span>
                      <p className="font-medium">{selectedVisita.motivo || selectedVisita.tipo_visita_nombre || "-"}</p>
                    </div>
                  </div>
                </div>

                {/* Tiempos */}
                <div className="p-4 bg-green-50 rounded-lg space-y-2">
                  <h4 className="font-medium text-green-900">Registro de Tiempo</h4>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <span className="text-green-600">Entrada:</span>
                      <p className="font-medium">{formatDateTime(selectedVisita.fecha_entrada)}</p>
                    </div>
                    <div>
                      <span className="text-green-600">Salida:</span>
                      <p className="font-medium">{formatDateTime(selectedVisita.fecha_salida)}</p>
                    </div>
                    <div>
                      <span className="text-green-600">Duración:</span>
                      <p className="font-medium">{calcularDuracion(selectedVisita.fecha_entrada, selectedVisita.fecha_salida)}</p>
                    </div>
                  </div>
                </div>

                {/* Vehículo y equipos */}
                {(selectedVisita.vehiculo_placa || selectedVisita.equipos) && (
                  <div className="p-4 bg-amber-50 rounded-lg space-y-2">
                    <h4 className="font-medium text-amber-900">Información Adicional</h4>
                    <div className="text-sm space-y-1">
                      {selectedVisita.vehiculo_placa && (
                        <div className="flex items-center gap-2">
                          <Car className="w-4 h-4 text-amber-600" />
                          <span>{selectedVisita.vehiculo_marca} - {selectedVisita.vehiculo_placa}</span>
                        </div>
                      )}
                      {selectedVisita.equipos && (
                        <div>
                          <span className="text-amber-600">Equipos:</span>
                          <p>{selectedVisita.equipos}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
